import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, switchMap} from 'rxjs/operators';

import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {MatInputModule} from '@angular/material/input';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatFormFieldModule} from '@angular/material/form-field';
import {NgIf, NgFor, AsyncPipe} from '@angular/common';
import {HttpParams} from '@angular/common/http';
import {UntypedFormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TranslateModule} from '@ngx-translate/core';
import {Q9HtmlEncoderPipe} from '@q9elements/ui-kit/common';
import {DiagramJsonHandlerService} from '../../../diagram-data/diagram-json-handler.service';
import {NavigationService} from '../../../core/navigation/navigation.service';
import {ModeDataService} from '../../../editor-state/mode-data.service';
import {CustomEncoder} from '../../../core/helpers/custom-encoder';
import {PopupManagerService} from '../../../core/services/popup-manager.service';
import {MapService} from '../../../core/map/map.service';
import {RequestTask} from 'src/app/shared/models/request-task.interface';
import {ClickOutsideDirective} from '../../shared/directives/click-outside.directive';
import {RequestsService} from '../../../core/requests/requests.service';
import {DIAGRAM_VERSION_STATES} from 'src/app/shared/models/diagram-version.interface';

@Component({
  selector: 'app-search-diagram',
  templateUrl: './search-diagram.component.html',
  styleUrls: ['./search-diagram.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    ClickOutsideDirective,
    NgFor,
    MatTooltipModule,
    AsyncPipe,
    TranslateModule,
    Q9HtmlEncoderPipe
  ]
})
export class SearchDiagramComponent implements OnInit, AfterContentChecked {
  constructor(
    private requestsSvc: RequestsService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private navSvc: NavigationService,
    private modeSvc: ModeDataService,
    private popupSvc: PopupManagerService,
    private mapService: MapService
  ) {}

  isProActive: boolean;
  diagramSearchSubject: BehaviorSubject<any> = new BehaviorSubject<any>({});
  diagramSearch$: Observable<any> = this.diagramSearchSubject.asObservable();
  menuSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  menuOpened$: Observable<boolean> = this.menuSubject.asObservable();

  searchControl = new UntypedFormControl('');
  searchFocused;
  showProFeature;
  clearInputSub: Subscription;
  proFeatureMsg: string;
  isLoading;
  isPro: boolean;
  isProTrial: boolean;
  isProTrialActive: boolean;

  @ViewChild('searchTemplate') inputTemplate;
  @ViewChild('searchCompTemplate') searchCompTemplate;
  @ViewChild('clearButton') clearButton;
  @ViewChildren('diagramName') diagramTitles: QueryList<ElementRef>;
  @ViewChildren('diagramMapName') diagramMapNames: QueryList<ElementRef>;

  @Input() state?: string;

  ngOnInit() {
    this.setupPlanTypeObservable();
    this.setObserverOnSearch();
  }

  ngAfterContentChecked() {
    if (this.diagramTitles) {
      this.diagramTitles.map(elRef => {
        const el = elRef.nativeElement;

        el.tooltipDisabled = el.offsetWidth >= el.scrollWidth;
      });
    }

    if (this.diagramMapNames) {
      this.diagramMapNames.map(elRef => {
        const el = elRef.nativeElement;

        el.tooltipDisabled = el.offsetWidth >= el.scrollWidth;
      });
    }
  }

  clearSearchInput() {
    this.searchFocused = false;
    this.searchControl.patchValue('');
    this.diagramSearchSubject.next({});
  }

  setupPlanTypeObservable() {
    this.isProActive = this.diagramJsonSvc.isProActive();
    this.isProTrial = this.diagramJsonSvc.isProTrial();
    this.isProTrialActive = this.diagramJsonSvc.isProTrialActive();
    this.proFeatureMsg = this.getProFeatureMsg();
  }

  setObserverOnSearch() {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        filter(value => !!value && this.isProActive)
      )
      .pipe(
        switchMap(text => {
          return this.requestsSvc.addToQueue(this.taskForSearch(text), true).catch(error => {
            switch (error.error.status) {
              case 'dont_filled_required_fields':
                this.popupSvc.openErrorDialog({
                  message: 'DTA.MODEL_CHANGED',
                  callback: 'navigateToDashboard'
                });
                break;

              case 'element_not_exists':

              case 'organization.not_found':
                this.popupSvc.openErrorDialog({
                  message: 'POPUPS.NO_ACCESS',
                  callback: 'navigateToDashboard'
                });
            }
          });
        })
      )
      .subscribe(result => {
        this.isLoading = false;

        if (result) {
          this.diagramSearchSubject.next(result);
          this.menuSubject.next(true);
        }
      });
  }

  taskForSearch(text): RequestTask {
    const state =
      this.state === DIAGRAM_VERSION_STATES.MASTER ? this.state : DIAGRAM_VERSION_STATES.DRAFT;
    const params = new HttpParams({encoder: new CustomEncoder()})
      .set('map', this.diagramJsonSvc.get().map.id)
      .set('query', text)
      .set('type', this.mapService.mapType)
      .set('state', state);

    return {
      isApi: true,
      method: 'GET',
      url: `/search/map`,
      queryParams: params
    };
  }

  openDiagram(diagram) {
    this.menuSubject.next(false);
    this.clearSearchInput();
    this.navSvc.navigate(diagram.diagramId, diagram.state, this.modeSvc.isEditMode());
  }

  catchClickOutside(event) {
    if (
      (event.target !== this.inputTemplate.nativeElement &&
        !this.isDescendant(this.searchCompTemplate.nativeElement, event.target)) ||
      (this.clearButton &&
        this.isDescendant(this.clearButton._elementRef.nativeElement, event.target))
    ) {
      this.menuSubject.next(false);
      this.clearSearchInput();
    }
  }

  isDescendant(parent, child) {
    let node = child;

    while (node !== null) {
      if (node === parent) {
        return true;
      } else {
        node = node.parentNode;
      }
    }

    return false;
  }

  get searchIsEmpty() {
    return this.searchControl.value === '';
  }

  onFocus() {
    this.searchFocused = true;

    if (!this.isProActive) {
      this.showProFeature = true;
      this.menuSubject.next(true);
      this.setupClearInputObserver();
    }
  }

  getProFeatureMsg() {
    let msg = 'SEARCH.UPGRADE_MESSAGE';

    if (this.isProTrial && !this.isProTrialActive) {
      msg = 'SEARCH.UPGRADE_MESSAGE_TRIAL_EXPIRED';
    }

    return msg;
  }

  onBlur() {
    this.searchFocused = false;

    if (this.isProActive && this.clearInputSub) {
      this.clearInputSub.unsubscribe();
    }
  }

  setupClearInputObserver() {
    if (!this.clearInputSub) {
      this.clearInputSub = this.searchControl.valueChanges
        .pipe(debounceTime(400), distinctUntilChanged())
        .subscribe(() => this.searchControl.patchValue(''));
    }
  }
}
